import React from 'react';

export type SealedBidVariant =
  | 'pre-submission'
  | 'submitted'
  | 'anonymous'
  | 'first-quote'
  | 'customer-viewer';

interface SealedBidPanelProps {
  variant: SealedBidVariant;
  quoteCount: number;
}

// Branded callout that makes QuoteBid's sealed-bid model visible in the UI.
// Competing contractor quotes are redacted server-side (BI1); this panel
// explains WHY a contractor/anonymous viewer sees no rival bids, so the
// hidden-quotes experience reads as intentional integrity rather than a bug.
const SealedBidPanel: React.FC<SealedBidPanelProps> = ({ variant, quoteCount }) => {
  // A logged-in homeowner browsing someone else's request can't bid and does
  // not need sealed-bid education — just a muted note, no 🔒 trust messaging.
  if (variant === 'customer-viewer') {
    return (
      <div style={styles.mutedPanel}>
        <p style={styles.mutedNote}>Only contractors can submit quotes on this request.</p>
      </div>
    );
  }

  // Copy locked verbatim per variant. Held in JS strings (not JSX text) so the
  // apostrophes don't trip react/no-unescaped-entities under CI builds.
  let lead = '';
  let body = '';
  switch (variant) {
    case 'pre-submission':
      lead = `Quotes submitted: ${quoteCount}`;
      body =
        "This is a sealed-bid request. Competing contractor quotes are hidden to protect bid integrity. Submit your best quote based on the job details, not another contractor's price.";
      break;
    case 'submitted':
      lead = 'Your quote has been submitted.';
      body =
        'This is a sealed-bid request. Competing contractor quotes are hidden to protect bid integrity.';
      break;
    case 'anonymous':
      lead = `Quotes submitted: ${quoteCount}`;
      body =
        'This is a sealed-bid request. Competing contractor quotes are hidden to protect bid integrity. Sign in to submit your quote on this request.';
      break;
    case 'first-quote':
      lead = 'Be the first to quote this request.';
      body =
        "This request uses QuoteBid's sealed-bid process. Once contractors begin submitting quotes, competing quote details stay hidden to protect bid integrity.";
      break;
  }

  return (
    <div style={styles.panel}>
      <div style={styles.headingRow}>
        <span style={styles.lockIcon} aria-hidden="true">🔒</span>
        <h3 style={styles.heading}>Sealed Bid Request</h3>
      </div>
      <p style={styles.lead}>{lead}</p>
      <p style={styles.body}>{body}</p>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  panel: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '10px',
    padding: '22px 24px',
    marginBottom: '20px',
  },
  headingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  lockIcon: {
    fontSize: '22px',
    lineHeight: 1,
  },
  heading: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#92400e',
  },
  lead: {
    margin: '0 0 8px 0',
    fontSize: '15px',
    fontWeight: 600,
    color: '#0f172a',
  },
  body: {
    margin: 0,
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#475569',
  },
  mutedPanel: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '18px 20px',
    marginBottom: '20px',
  },
  mutedNote: {
    margin: 0,
    fontSize: '14px',
    color: '#64748b',
    lineHeight: 1.5,
  },
};

export default SealedBidPanel;
